const CAMPAIGN_FIELDS = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "oppref",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const ATTRIBUTION_STORAGE_KEY = "sb_signup_attribution";
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_URL_LENGTH = 2000;
const isBrowser =
  typeof window !== "undefined" && typeof document !== "undefined";

export const isSalesBinderHostname = (hostname) => {
  const host = String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
  return host === "salesbinder.com" || host.endsWith(".salesbinder.com");
};

export const normalizeAttributionUrl = (value) => {
  if (!value) return "";
  try {
    const url = new URL(String(value).trim());
    if (
      !["http:", "https:"].includes(url.protocol) ||
      url.username ||
      url.password
    )
      return "";
    url.hash = "";
    return url.toString().slice(0, MAX_URL_LENGTH);
  } catch {
    return "";
  }
};

const externalReferrer = (value) => {
  const url = normalizeAttributionUrl(value);
  return url && !isSalesBinderHostname(new URL(url).hostname) ? url : "";
};
const nonDirect = (touch) =>
  Boolean(touch.referrer_url || CAMPAIGN_FIELDS.some((field) => touch[field]));

export const paidProvider = (record) => {
  if (!record || typeof record !== "object" || Array.isArray(record))
    return null;
  if (record.oppref) return "openai";
  if (record.gclid || record.gbraid || record.wbraid) return "google";
  if (record.msclkid) return "microsoft";
  const source = String(record.utm_source || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "")
    .replace(/^www\./, "");
  const medium = String(record.utm_medium || "")
    .trim()
    .toLowerCase();
  if (
    ![
      "cpc",
      "ppc",
      "paid",
      "paid_search",
      "paid_social",
      "paidsearch",
    ].includes(medium) ||
    !/^[a-z][a-z0-9_.-]{0,63}$/.test(source) ||
    ["constructor", "prototype", "__proto__"].includes(source)
  )
    return null;
  const aliases = {
    openai: "openai",
    "openai.com": "openai",
    chatgpt: "openai",
    "chatgpt.com": "openai",
    "chat.openai.com": "openai",
    google: "google",
    "google.com": "google",
    bing: "microsoft",
    "bing.com": "microsoft",
    microsoft: "microsoft",
    "microsoft.com": "microsoft",
    capterra: "capterra",
    "capterra.com": "capterra",
    g2: "g2",
    "g2.com": "g2",
    getapp: "getapp",
    "getapp.com": "getapp",
  };
  return Object.hasOwn(aliases, source) ? aliases[source] : source;
};

export const captureFirstTouchAttribution = ({
  existing = {},
  locationHref = "",
  locationSearch = "",
  referrerUrl = "",
  now = Date.now(),
}) => {
  const saved =
    existing &&
    typeof existing === "object" &&
    !Array.isArray(existing) &&
    (!existing._expires_at || existing._expires_at > now)
      ? existing
      : {};
  const attribution = { ...saved, version: 2 };
  const query = new URLSearchParams(locationSearch);
  const touch = {
    landing_url: normalizeAttributionUrl(locationHref),
    observed_at: new Date(now).toISOString(),
    tracking_version: 3,
  };
  const referrer = externalReferrer(referrerUrl);
  if (referrer) touch.referrer_url = referrer;
  CAMPAIGN_FIELDS.forEach((field) => {
    const value = (query.get(field) || "").trim();
    if (field === "oppref") {
      const raw = query.get(field) || "";
      if (raw && raw.length <= 2048 && !/[\s\x00-\x1f]/.test(raw))
        touch[field] = raw;
    } else if (value) touch[field] = value.slice(0, 255);
  });

  // Old records may contain fields from multiple visits. Retain them without a fabricated date.
  if (
    !attribution.first_touch &&
    Object.keys(saved).some((field) =>
      [...CAMPAIGN_FIELDS, "sb_landing_url", "sb_referrer_url"].includes(field),
    )
  ) {
    const legacy = { legacy: true };
    CAMPAIGN_FIELDS.forEach((field) => {
      if (saved[field]) legacy[field] = saved[field];
    });
    if (saved.sb_landing_url)
      legacy.landing_url = normalizeAttributionUrl(saved.sb_landing_url);
    if (externalReferrer(saved.sb_referrer_url))
      legacy.referrer_url = externalReferrer(saved.sb_referrer_url);
    attribution.first_touch = legacy;
  }
  attribution.first_touch ||= touch;
  const first = attribution.first_touch;
  if (!attribution.first_external_referral) {
    if (first.referrer_url) attribution.first_external_referral = { ...first };
    else if (touch.referrer_url)
      attribution.first_external_referral = { ...touch };
  }
  if (!attribution.latest_non_direct_touch && nonDirect(first)) {
    attribution.latest_non_direct_touch = { ...first };
  }
  if (
    nonDirect(touch) &&
    (!attribution.latest_non_direct_touch?.observed_at ||
      touch.observed_at > attribution.latest_non_direct_touch.observed_at)
  ) {
    attribution.latest_non_direct_touch = touch;
  }
  const paidTouches = {};
  const retainPaidTouch = (record) => {
    const provider = paidProvider(record);
    if (!provider) return;
    const current = paidTouches[provider];
    if (!current || (record.observed_at || "") > (current.observed_at || "")) {
      paidTouches[provider] = { ...record };
    }
  };
  const savedPaid = attribution.latest_paid_touches;
  if (savedPaid && typeof savedPaid === "object" && !Array.isArray(savedPaid)) {
    Object.entries(savedPaid)
      .slice(0, 20)
      .forEach(([provider, record]) => {
        if (paidProvider(record) === provider) retainPaidTouch(record);
      });
  }
  [first, attribution.latest_non_direct_touch, touch].forEach(retainPaidTouch);
  if (Object.keys(paidTouches).length)
    attribution.latest_paid_touches = paidTouches;
  else delete attribution.latest_paid_touches;
  attribution._expires_at ||= now + ATTRIBUTION_TTL_MS;
  // Compatibility for an older signup backend; never mix fields from different visits.
  CAMPAIGN_FIELDS.forEach((field) => {
    delete attribution[field];
    if (first[field]) attribution[field] = first[field];
  });
  attribution.sb_landing_url = first.landing_url || "";
  attribution.sb_referrer_url = first.referrer_url || "";
  return attribution;
};

const readStorageValue = (storage) => {
  const value = JSON.parse(storage?.getItem(ATTRIBUTION_STORAGE_KEY) || "{}");
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  if (value._expires_at && value._expires_at <= Date.now()) {
    storage.removeItem(ATTRIBUTION_STORAGE_KEY);
    return {};
  }
  return value;
};
const readStoredAttribution = () => {
  if (!isBrowser) return {};
  try {
    const value = readStorageValue(window.localStorage);
    if (Object.keys(value).length) return value;
  } catch {
    /* Retain existing session-storage fallback. */
  }
  try {
    return readStorageValue(window.sessionStorage);
  } catch {
    return {};
  }
};

let lastCapturedHref = null;
const captureAttribution = (location) => {
  if (!isBrowser || !location) return readStoredAttribution();
  const href =
    location.href ||
    new URL(
      `${location.pathname || "/"}${location.search || ""}`,
      window.location.origin,
    ).href;
  if (href === lastCapturedHref) return readStoredAttribution();
  // document.referrer remains unchanged during Gatsby navigation; observe it only on document entry.
  const referrerUrl = lastCapturedHref === null ? document.referrer : "";
  lastCapturedHref = href;
  const attribution = captureFirstTouchAttribution({
    existing: readStoredAttribution(),
    locationHref: href,
    locationSearch: location.search,
    referrerUrl,
  });
  try {
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(attribution),
    );
  } catch {
    try {
      window.sessionStorage.setItem(
        ATTRIBUTION_STORAGE_KEY,
        JSON.stringify(attribution),
      );
    } catch {
      /* Signup remains usable. */
    }
  }
  return attribution;
};

export const buildAttributedSignupUrl = (
  href,
  attribution,
  currentHref = "https://www.salesbinder.com/",
) => {
  const url = new URL(href, currentHref);
  if (
    url.origin !== "https://app.salesbinder.com" ||
    !(url.pathname === "/signup" || url.pathname.startsWith("/signup/"))
  )
    return url.toString();
  [...CAMPAIGN_FIELDS, "sb_landing_url", "sb_referrer_url"].forEach((field) => {
    if (attribution[field] && !url.searchParams.has(field))
      url.searchParams.set(field, attribution[field]);
  });
  if (attribution.first_touch) {
    const payload = { version: 2, first_touch: attribution.first_touch };
    [
      "first_external_referral",
      "latest_non_direct_touch",
      "latest_paid_touches",
    ].forEach((field) => {
      if (attribution[field]) payload[field] = attribution[field];
    });
    url.searchParams.set("sb_attribution", JSON.stringify(payload));
  }
  return url.toString();
};

let renderingReady = false;
export const attributedSignupUrl = (href) =>
  renderingReady
    ? buildAttributedSignupUrl(href, readStoredAttribution())
    : href;
const decoratedLinks = new WeakMap();
const decorateSignupLink = (link, attribution) => {
  if (!link?.href) return;
  const previous = decoratedLinks.get(link);
  const original =
    previous?.decorated === link.href ? previous.original : link.href;
  const decorated = buildAttributedSignupUrl(
    original,
    attribution,
    window.location.href,
  );
  decoratedLinks.set(link, { original, decorated });
  if (decorated !== link.href) link.href = decorated;
};

export const syncSignupAttribution = (location) => {
  if (!isBrowser) return;
  const attribution = captureAttribution(location || window.location);
  if (renderingReady)
    document
      .querySelectorAll("a[href]")
      .forEach((link) => decorateSignupLink(link, attribution));
};
let initialized = false;
export const initializeSignupAttribution = () => {
  if (!isBrowser || initialized) return;
  initialized = true;
  captureAttribution(window.location);
  const decorateEvent = (event) => {
    const link = event.target.closest?.("a[href]");
    decorateSignupLink(link, readStoredAttribution());
  };
  ["pointerdown", "click", "auxclick", "contextmenu", "focusin"].forEach(
    (type) => document.addEventListener(type, decorateEvent, true),
  );
};

export const startSignupLinkSync = () => {
  if (!isBrowser || renderingReady) return;
  renderingReady = true;
  syncSignupAttribution(window.location);
  // Synchronize links inserted or replaced by React before the next user interaction.
  const observer = new MutationObserver((mutations) => {
    const attribution = readStoredAttribution();
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes")
        decorateSignupLink(mutation.target, attribution);
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches("a[href]")) decorateSignupLink(node, attribution);
        node
          .querySelectorAll("a[href]")
          .forEach((link) => decorateSignupLink(link, attribution));
      });
    });
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["href"],
  });
};
